/* eslint-disable no-use-before-define */
import type React from 'react';

import type { WidgetResponse } from '@lumapps/widget-base/api';
import type { LegacyWidget } from '@lumapps/widget-base/types';

import type { RowStyle, CellStyle } from './styles';

/** Layout */
export interface Layout {
    /** Nested rows. */
    components: Row[];
    /** Layout properties. */
    properties?: {
        /** HTML id attribute. */
        id?: string;
        /** HTML class attribute. */
        class?: string;
    };
    /** The pre-loaded display blocks for the widgets present in the layout. */
    widgets?: WidgetResponse[];
    /** layout id */
    id?: string;
    /** Whether the content is V2 compatible */
    isV2Compatible?: boolean;
}

/** Row */
export interface Row {
    children?: React.ReactNode;
    type: 'row';
    /** Nested cells. */
    cells: Cell[];
    /** Row properties. */
    properties?: {
        /** HTML id attribute. */
        id?: string;
        /** HTML class attribute. */
        class?: string;
        /** Row style. */
        style?: RowStyle;
        /** Row takes the full width of the page. */
        fullWidth?: boolean;
        /** Overriding row size. */
        sizeVariant?: SizeVariant;
    };
}
export const isRow = (node: any): node is Row => node && node.type === 'row';

/** Cell */
export interface Cell {
    children?: React.ReactNode;
    type: 'cell';
    /** Nested rows & widgets. */
    components: Array<Row | WidgetReference>;
    /** Cell width (min = 1, max = 12). */
    width?: number;
    /** Cell properties. */
    properties?: {
        /** HTML id attribute. */
        id?: string;
        /** HTML class attribute. */
        class?: string;
        /** Cell style. */
        style?: { components?: CellStyle };
        /** Cell gets a default background and border and its widgets don’t. */
        plain?: boolean;
        /** Cell remains visible when scrolling down the page. */
        sticky?: boolean;
    };
}
export const isCell = (node: any): node is Cell => node && node.type === 'cell';

/** Widget reference */
export interface WidgetReference<WidgetSpecificProperties = object> {
    /** Node type in layout tree. */
    type: 'widget';
    /** The widget's id. */
    widgetId: string;
    /** The widget's type. */
    widgetType: string;
    /** Legacy widget (only available in content preview) */
    legacyWidget?: LegacyWidget<WidgetSpecificProperties>;
    /** Is main widget */
    isMainWidget?: boolean;
}

/**
 * Path of element in the layout (nesting in rows, cells and with widgets as leafs)
 * @example
 *   `['row0']` => Path to the first row
 *   `['row0', 'cell1']` => Path to the second cell of the first row
 *   `['row0', 'cell1', 'widget0']` => Path to the first widget in the second cell of the first row
 *   Cell path can also nest another row level (as defined in {@link Cell}).
 */
export type LayoutPath = Array<`row${number}` | `cell${number}` | `widget${number}`>;

/**
 * Props for Widget override component
 */
export interface WidgetOverridesProps<WidgetSpecificProperties = object> {
    /** Widget reference in layout */
    widgetReference: Omit<WidgetReference<WidgetSpecificProperties>, 'type'>;
    /** Widget path in template */
    path: LayoutPath;
    /** Overrides the widget content */
    children?: React.ReactNode;
}

/**
 * Mapping from widget types to widget override components
 */
export type WidgetOverrides = {
    [widgetType: string]: React.FC<WidgetOverridesProps<any>>;
} & {
    /** Provide a default widget override when no previous widget type matches */
    default?: React.FC<WidgetOverridesProps>;
};

export enum SizeVariant {
    SMALL = 'small',
    COMPACT = 'compact',
    REGULAR = 'regular',
    FULL_WIDTH = 'full-width',
}

export interface LayoutOptions {
    /** Restricted list of widget types */
    enabledWidgetTypes?: string[];
}

export interface LayoutProps extends Partial<Layout> {
    /** Page content ref. */
    contentRef?: React.Ref<HTMLDivElement>;
    /** Widget overrides by widget type (used to inject alternative version in content editor) */
    widgetOverrides?: WidgetOverrides;
}
